import React from "react";
import {
  Clock3,
  MapPinned,
  PackageCheck,
  ShieldCheck,
} from "lucide-react";

export default function NormalDeliverySettings({
  activeValues,
  setActiveValues,
  Field,
  TextInput,
  Toggle,
  SectionCard,
  parseNumberField,
  lockCoreDeliveryFields = false,
}) {
  const updateNumber = (key) => (event) => {
    const nextValue = parseNumberField(event.target.value);
    setActiveValues((previous) => ({
      ...previous,
      [key]: nextValue,
    }));
  };

  return (
    <div className="space-y-5">
      <div className="grid items-stretch gap-5 xl:grid-cols-2">
        <SectionCard
          icon={PackageCheck}
          title="Driver Assignment"
          description="Set how many assigned orders a driver may hold. These limits affect assignment only; the one-running-delivery protection stays system controlled."
        >
          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-3">
            <Field
              label="Maximum Total Assigned Orders"
              hint="Orders · Default 5"
              info="Maximum active, non-delivered assigned orders of all types combined that one driver can hold."
            >
              <TextInput
                type="number"
                min={1}
                value={activeValues.defaultMaxActiveOrders}
                onChange={updateNumber("defaultMaxActiveOrders")}
              />
            </Field>

            <Field
              label="Maximum Food Assigned Orders"
              hint="Orders · Default 2"
              info="Maximum active food/restaurant orders assigned to one driver. Food + Grocery limits together must not exceed Maximum Total Assigned Orders."
            >
              <TextInput
                type="number"
                min={1}
                value={activeValues.maxFoodOrdersPerPartner}
                onChange={updateNumber("maxFoodOrdersPerPartner")}
              />
            </Field>

            <Field
              label="Maximum Grocery Assigned Orders"
              hint="Orders · Default 3"
              info="Maximum active grocery orders assigned to one driver. Food + Grocery limits together must not exceed Maximum Total Assigned Orders."
            >
              <TextInput
                type="number"
                min={1}
                value={activeValues.maxGroceryOrdersPerPartner}
                onChange={updateNumber("maxGroceryOrdersPerPartner")}
              />
            </Field>
          </div>

          <div className="mt-5 rounded-[14px] border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500">
            Food + Grocery assigned-order limits must fit inside the Maximum Total Assigned Orders. Example: Total 5 allows Food 2 + Grocery 3. Driver task choice is unchanged; only one picked-up/out-for-delivery task may run at a time.
          </div>
        </SectionCard>

        <SectionCard
          icon={PackageCheck}
          title="Food Assignment"
          description="Food-specific assignment and pre-payment reservation controls."
        >
          <div className="space-y-5">
            <Toggle
              label="Block Assignment If Food Awaiting Pickup"
              checked={!!activeValues.blockAssignmentWhileFoodPickupPending}
              onChange={(next) =>
                setActiveValues((previous) => ({
                  ...previous,
                  blockAssignmentWhileFoodPickupPending: next,
                }))
              }
            />

            <Field
              label="Food Driver Reservation Time"
              hint="Minutes · Default 5"
              info="How long a driver reservation is held during food checkout before it expires if payment/confirmation does not complete."
            >
              <TextInput
                type="number"
                min={1}
                max={1440}
                value={activeValues.foodDriverReservationMinutes}
                onChange={updateNumber("foodDriverReservationMinutes")}
              />
            </Field>
          </div>
        </SectionCard>
      </div>

      <SectionCard
        icon={MapPinned}
        title="Pickup Search"
        description="Use separate driver-search radii for grocery warehouses and food restaurants. Search starts at the initial radius and expands to the maximum only when needed."
      >
        <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-5">
          <Field
            label="Grocery Initial Search Radius"
            hint="Km"
            info="First warehouse-to-driver pickup radius used for grocery assignment."
          >
            <TextInput
              type="number"
              min={0.1}
              value={activeValues.groceryInitialSearchKm}
              onChange={updateNumber("groceryInitialSearchKm")}
            />
          </Field>

          <Field
            label="Grocery Maximum Search Radius"
            hint="Km · Must be ≥ initial"
            info="Maximum warehouse-to-driver pickup radius used only when no eligible driver is found in the initial grocery radius."
          >
            <TextInput
              type="number"
              min={0.1}
              value={activeValues.groceryMaxSearchKm}
              onChange={updateNumber("groceryMaxSearchKm")}
            />
          </Field>

          <Field
            label="Food Initial Search Radius"
            hint="Km"
            info="First restaurant-to-driver pickup radius used for food assignment."
          >
            <TextInput
              type="number"
              min={0.1}
              value={activeValues.foodInitialSearchKm}
              onChange={updateNumber("foodInitialSearchKm")}
            />
          </Field>

          <Field
            label="Food Maximum Search Radius"
            hint="Km · Must be ≥ initial"
            info="Maximum restaurant-to-driver pickup radius used only when no eligible driver is found in the initial food radius."
          >
            <TextInput
              type="number"
              min={0.1}
              value={activeValues.foodMaxSearchKm}
              onChange={updateNumber("foodMaxSearchKm")}
            />
          </Field>

          <Field
            label="Maximum Candidate Drivers"
            hint="Drivers · Default 25"
            info="Maximum nearby eligible drivers evaluated during one assignment attempt."
          >
            <TextInput
              type="number"
              min={1}
              value={activeValues.maxCandidates}
              onChange={updateNumber("maxCandidates")}
            />
          </Field>
        </div>
      </SectionCard>

      <div className="grid items-stretch gap-5 xl:grid-cols-2">
        <SectionCard
          icon={MapPinned}
          title="Order Coverage"
          description="Customer serviceability is separate from the pickup-driver search radii above."
        >
          <div className="space-y-5">
            <Toggle
              label="Allow city-based fallback search"
              checked={!!activeValues.searchCityFallback}
              onChange={(next) =>
                setActiveValues((previous) => ({
                  ...previous,
                  searchCityFallback: next,
                }))
              }
            />

            <Field
              label="Order Radius"
              hint="Km · Recommended 8"
              info="Maximum customer-to-warehouse distance for accepting a grocery order."
            >
              <TextInput
                type="number"
                min={0}
                value={activeValues.defaultOrderKm}
                onChange={updateNumber("defaultOrderKm")}
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard
          icon={ShieldCheck}
          title="Driver Presence"
          description="Decide how recent a driver location must be before assignment."
        >
          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
            <Field
              label="Location Freshness"
              hint="Seconds · Driver ping 25s"
              info="A driver is considered fresh only when the latest location update is within this time window."
            >
              <TextInput
                type="number"
                min={0}
                disabled={lockCoreDeliveryFields}
                value={activeValues.recentSeconds}
                onChange={updateNumber("recentSeconds")}
              />
            </Field>

            <Field
              label="Online Presence TTL"
              hint="Seconds · Must be ≥ freshness"
              info="How long driver presence remains live after the latest location update."
            >
              <TextInput
                type="number"
                min={0}
                disabled={lockCoreDeliveryFields}
                value={activeValues.riderOnlineTtlSeconds}
                onChange={updateNumber("riderOnlineTtlSeconds")}
              />
            </Field>
          </div>

          <div className="mt-5 rounded-[14px] border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500">
            Driver and approval statuses are system-controlled and intentionally hidden from this page.
          </div>
        </SectionCard>
      </div>

      <div className="grid items-stretch gap-5 xl:grid-cols-2">
        <SectionCard
          icon={Clock3}
          title="Cart and Cache Timing"
          description="Control cart reservation time and short-lived availability caches."
        >
          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
            <Field
              label="Cart Hold"
              hint="Minutes"
              info="How long reserved cart stock remains locked before it is released."
            >
              <TextInput
                type="number"
                min={0}
                disabled={lockCoreDeliveryFields}
                value={activeValues.cartHoldMinutes}
                onChange={updateNumber("cartHoldMinutes")}
              />
            </Field>

            <Field
              label="Serviceability Cache"
              hint="Seconds · Recommended 30"
              info="Caches whether an address and warehouse combination is serviceable."
            >
              <TextInput
                type="number"
                min={0}
                disabled={lockCoreDeliveryFields}
                value={activeValues.serviceabilityTtlSeconds}
                onChange={updateNumber("serviceabilityTtlSeconds")}
              />
            </Field>

            <Field
              label="Driver Availability Cache"
              hint="Seconds · Recommended 15"
              info="Caches nearby-driver availability briefly to reduce repeated checks without holding stale results too long."
            >
              <TextInput
                type="number"
                min={0}
                disabled={lockCoreDeliveryFields}
                value={activeValues.deliveryPartnerTtlSeconds}
                onChange={updateNumber("deliveryPartnerTtlSeconds")}
              />
            </Field>

            <Field
              label="Product Availability Cache"
              hint="Seconds · Recommended 30"
              info="Caches product stock and availability responses."
            >
              <TextInput
                type="number"
                min={0}
                disabled={lockCoreDeliveryFields}
                value={activeValues.productAvailabilityTtlSeconds}
                onChange={updateNumber("productAvailabilityTtlSeconds")}
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard
          icon={Clock3}
          title="Assignment Recovery — Advanced"
          description="Controls automatic retry for orders that could not get a driver. Change only when you understand the operational impact."
        >
          <div className="space-y-5">
            <Toggle
              label="Auto Retry"
              checked={!!activeValues.dispatchRetryEnabled}
              onChange={(next) =>
                setActiveValues((previous) => ({
                  ...previous,
                  dispatchRetryEnabled: next,
                }))
              }
            />

            <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
              <Field
                label="Retry Interval"
                hint="Seconds · Minimum 60"
                info="How often the dispatch recovery worker checks orders that are still waiting for a driver."
              >
                <TextInput
                  type="number"
                  min={60}
                  value={activeValues.dispatchRetryIntervalSeconds}
                  onChange={updateNumber("dispatchRetryIntervalSeconds")}
                />
              </Field>

              <Field
                label="Retry When Stale After"
                hint="Seconds · Default 60"
                info="An unassigned dispatch must be at least this old before the recovery worker retries it."
              >
                <TextInput
                  type="number"
                  min={10}
                  value={activeValues.dispatchRetryStaleAfterSeconds}
                  onChange={updateNumber("dispatchRetryStaleAfterSeconds")}
                />
              </Field>

              <Field
                label="Maximum Retry Age"
                hint="Minutes · Default 180"
                info="Stops automatic retry when an unassigned dispatch is older than this limit."
              >
                <TextInput
                  type="number"
                  min={5}
                  value={activeValues.dispatchRetryMaxAgeMinutes}
                  onChange={updateNumber("dispatchRetryMaxAgeMinutes")}
                />
              </Field>

              <Field
                label="Retry Batch Size"
                hint="Orders · 1–100"
                info="Maximum waiting orders processed by one recovery sweep."
              >
                <TextInput
                  type="number"
                  min={1}
                  max={100}
                  value={activeValues.dispatchRetryBatchSize}
                  onChange={updateNumber("dispatchRetryBatchSize")}
                />
              </Field>

              <Field
                label="Maximum Attempts"
                hint="Attempts · Default 30"
                info="Maximum dispatch recovery attempts recorded for one order before automatic recovery stops."
              >
                <TextInput
                  type="number"
                  min={1}
                  value={activeValues.dispatchRetryMaxAttempts}
                  onChange={updateNumber("dispatchRetryMaxAttempts")}
                />
              </Field>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
